import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ECOMMERCES, Shop } from '@app/services/shops/shop.model';
import { CountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { formatNumberString, getTimeZoneOffset } from 'src/utils';
import { isNumber } from 'src/utils/number-utils';
import { FieldType } from '../../../services/settings/settings.service';
import moment from 'moment';

export interface AccountField {
  name: string;
  label: string;
  dataName: string;
  type: FieldType;
  placeholder?: string;
  description?: string;
  default?: any;
  editable: boolean;
  clipboard?: boolean;
  tooltip?: string;
  min?: number;
  max?: number;
  formatter?: (any) => any;
  validators?;
  validationMsg?: string;
  displayCondition?: (countryCode: CountryCode, platform: ECOMMERCES, shopMetadata?: any) => boolean;
  generateSubForm?: (data: any, shop: Shop) => any;
  applySubForm?: (subForm: any) => { path: string; value: any }[] | null;
  templateRef?: string;
  nestedFormControl?: string;
  subFormFields?: AdditionalFields[];
}

export interface AdditionalFields {
  label?: string;
  dataName: string;
  type: FieldType;
  placeholder?: string;
  editable?: boolean;
  default?: any;
  options?: { label: string; value: string }[];
}

export const accountFields: AccountField[] = [
  {
    name: 'title',
    label: `Store name`,
    dataName: 'title',
    type: FieldType.INPUT,
    placeholder: 'Enter store name',
    tooltip: 'How your store appears inside PDQ',
    editable: true,
    validators: [Validators.required],
    validationMsg: 'Required',
  },
  {
    name: 'identifierToken',
    label: 'Identifier API token',
    dataName: 'identifierToken',
    type: FieldType.INPUT,
    editable: false,
    clipboard: true,
  },
  {
    name: 'name',
    label: 'Store URL',
    dataName: 'name',
    type: FieldType.INPUT,
    placeholder: 'https://www.prettydamnquick.io/',
    editable: false,
  },
  {
    name: 'email',
    label: 'Contact email',
    dataName: 'metadata.email',
    type: FieldType.INPUT,
    placeholder: 'user@domain.com',
    editable: true,
  },
  {
    name: 'logo',
    label: 'Store logo',
    dataName: 'logo',
    tooltip: '.png, maximum size 100KB',
    type: FieldType.FILE,
    editable: true,
  },
  {
    name: 'platform',
    label: 'eCommerce platform',
    dataName: 'platform',
    type: FieldType.INPUT,
    editable: false,
  },
  {
    name: 'currency',
    label: 'Store currency',
    dataName: 'metadata.currency',
    type: FieldType.INPUT,
    editable: false,
    tooltip: 'Set in your eCommerce store',
  },
  {
    name: 'industry',
    label: 'Industry',
    dataName: '',
    placeholder: 'Not set',
    type: FieldType.INPUT,
    editable: false,
    tooltip: 'Optional. Helps us tailor insights for your store.',
  },
  {
    name: 'defaultDeliveryPromise',
    label: 'Default delivery promise in business days',
    dataName: 'metadata.defaultDeliveryPromise',
    placeholder: 'Business days',
    type: FieldType.INPUT,
    editable: true,
    validators: [Validators.min(0.5), Validators.max(100)],
    validationMsg: 'Must be more than 0.5',
    tooltip: 'The default number of business days orders will be expected to arrive',
  },
  {
    name: 'expirationDaysNumber',
    label: 'Order archive period',
    dataName: 'expirationDaysNumber',
    type: FieldType.INPUT,
    placeholder: 'Business days',
    tooltip: 'How many days before orders are archived',
    editable: false,
  },
  {
    name: 'ianaTimeZone',
    label: 'Time zone',
    dataName: 'metadata.ianaTimeZone',
    type: FieldType.INPUT,
    formatter: (val) => val, //Intl.DateTimeFormat().resolvedOptions().timeZone,
    editable: false,
    tooltip: 'Set in your eCommerce store',
  },
  {
    name: 'locale',
    label: 'Language',
    dataName: 'metadata.locale',
    type: FieldType.INPUT,
    formatter: (val) => val, //languageNames.of(val),
    editable: false,
  },
  {
    name: 'customTrackingUrl',
    label: 'Custom tracking link',
    dataName: 'metadata.customTrackingUrl',
    placeholder: 'https://www.your-tracking-link.com/search/{tracking_number}',
    type: FieldType.INPUT,
    editable: true,
    validators: [Validators.pattern(/.*\{tracking_number\}.*/)],
    validationMsg: 'The URL must include {tracking_number}',
    tooltip: 'Add your own tracking URL',
  },
  {
    name: 'addOrdersToExistingBatches',
    label: 'Add orders to batches',
    description: 'Allow new orders to be added into existing batches',
    dataName: 'metadata.addOrdersToExistingBatches',
    type: FieldType.TOGGLE,
    editable: true,
    default: false,
  },
  {
    name: 'fulfillBatch',
    label: 'Fulfill all in batch',
    description: 'Add a “Fulfill all” option when processing batches',
    dataName: 'metadata.fulfillBatch',
    type: FieldType.TOGGLE,
    editable: true,
    default: true,
  },
  {
    name: 'pickingModeInAssign',
    label: 'Picking in Verify & Assign',
    description: 'Mark items as picked in Verify and Assign screens',
    dataName: 'metadata.pickingModeInAssign',
    type: FieldType.TOGGLE,
    editable: true,
    default: false,
  },
  {
    name: 'pickingMode',
    label: 'Picking in Fulfill',
    description: 'Mark items as picked instead of fulfilled in the Fulfill screen',
    dataName: 'metadata.pickingMode',
    type: FieldType.TOGGLE,
    editable: true,
    default: false,
  },
  {
    name: 'pickingModeLabelAutomation',
    label: 'Auto-buy labels',
    description: 'Automatically purchase a label once all items are picked and packed.',
    dataName: 'metadata.pickingModeLabelAutomation',
    type: FieldType.TOGGLE,
    editable: true,
    default: false,
  },
  /*{
    name: 'isMultiParcelSupport',
    label: 'Multi-parcel shipments',
    description: `Enables shipping multiple packages in single order`,
    dataName: 'metadata.isMultiParcelSupport,metadata.multiLabels',
    type: FieldType.TOGGLE,
    editable: true,
    displayCondition: (countryCode: CountryCode) => {
      if (!countryCode) return false;
      return [CountryCode.US, CountryCode.CA].includes(countryCode) ? true : false;
    },
  },
  {
    name: 'isMultiLabelSupport',
    label: 'Multi-label printing',
    description: `Enables printing multiple labels in single order`,
    dataName: 'metadata.multiLabels',
    type: FieldType.TOGGLE,
    editable: true,
    displayCondition: (countryCode: CountryCode) => {
      if (!countryCode) return false;
      return [CountryCode.IL].includes(countryCode) ? true : false;
    },
  },*/
  {
    name: 'supports_residential_indicator',
    label: 'Show address type',
    description: 'Label addresses as commercial or residential',
    dataName: 'metadata.supports_residential_indicator',
    type: FieldType.TOGGLE,
    editable: true,
  },
  {
    name: 'supportsTracingNumber',
    label: 'Tracing number support',
    description: `Add tracing numbrs to products`,
    dataName: 'metadata.supportsTracingNumber',
    type: FieldType.TOGGLE,
    editable: true,
  },
  {
    name: 'cancelFulfillmentOnBackToAssign',
    label: 'Cancel fulfillment when reassigned',
    description: 'If an order is moved back to Assign, cancel its fulfillment in Shopify',
    dataName: 'metadata.cancelFulfillmentOnBackToAssign',
    type: FieldType.TOGGLE,
    editable: true,
    displayCondition: (countryCode: CountryCode, platform: ECOMMERCES, meatdata: any) => {
      return platform === ECOMMERCES.SHOPIFY;
    },
  },
  {
    name: 'minLoyalCustomerOrdersAmount',
    label: 'Loyal customer threshold',
    description: 'Set the number of orders before a customer is marked as loyal',
    dataName: 'metadata.minLoyalCustomerOrdersAmount',
    type: FieldType.INPUT_ARROW,
    validators: [Validators.min(0)],
    default: 3,
    min: 0,
    editable: true,
    displayCondition: (countryCode: CountryCode, platform: ECOMMERCES) => {
      if (!countryCode) return false;
      return [CountryCode.US, CountryCode.CA, CountryCode.UK].includes(countryCode) ? true : false;
    },
  },
  {
    name: 'batchExpirationDays',
    label: 'Archive time in Fulfill',
    description: 'Number of days before batches move to history',
    dataName: 'metadata.batchExpirationDays',
    type: FieldType.INPUT_ARROW,
    validators: [Validators.min(4), Validators.max(40)],
    editable: true,
    default: 4,
    min: 4,
    max: 40,
  },
  {
    name: 'pickupCutoffTime',
    label: 'Pickup cutoff time',
    description: 'Set the latest time orders can be picked up before rolling over to the next day',
    dataName: 'metadata.pickupCutoffTime',
    type: FieldType.TIME_INPUT,
    validators: [],
    editable: true,
    generateSubForm: (cutoffTime: string, shop: Shop) => {
      try {
        let hours: number | null = null;
        let minutes: number | null = null;
        let units = 'AM';
        let date = cutoffTime ? new Date(cutoffTime) : null;
        const ianaTimeZone = shop?.metadata?.ianaTimeZone;
        if (!ianaTimeZone) {
          throw new Error(`Shop: ${shop?.id} missing valid timezone value: ${shop?.metadata?.ianaTimeZone}`);
        }
        const localMoment = moment.tz(date, ianaTimeZone);
        if (date) {
          date = new Date(
            localMoment.year(),
            localMoment.month(),
            localMoment.date(),
            localMoment.hour(),
            localMoment.minute(),
            localMoment.second(),
            localMoment.millisecond(),
          );
          const tempLocalHours = date.getHours();
          units = tempLocalHours >= 12 ? (tempLocalHours === 24 ? 'AM' : 'PM') : 'AM';
          hours = tempLocalHours % 12;
          hours = hours ? hours : 12;
          minutes = date.getMinutes();

          if (!isNumber(hours) || !isNumber(minutes) || !units) {
            throw new Error(`Failed to proccess cutoff time: ${cutoffTime}`);
          }
        }

        const subForm = new UntypedFormGroup(
          {
            hours: new UntypedFormControl(hours ? formatNumberString(hours?.toString()) : null, [
              Validators.min(1),
              Validators.max(12),
            ]),
            minutes: new UntypedFormControl(hours ? formatNumberString(minutes?.toString()) : null, [
              Validators.min(0),
              Validators.max(59),
            ]),
            units: new UntypedFormControl(units),
          },
          hoursValidator(),
        );

        subForm.valueChanges.subscribe((changes) => {
          const hoursChangesNum = isNumber(changes.hours) ? +changes.hours : NaN;

          if (changes.hours?.toString().length > 2) {
            subForm.controls.hours.setValue((+hoursChangesNum).toString());
          }

          const minutesChangesNum = isNumber(changes.minutes) ? +changes.minutes : NaN;

          if (changes.minutes?.toString().length > 2) {
            subForm.controls.minutes.setValue((+minutesChangesNum).toString());
          }
        });

        return {
          pickupCutoffTime: subForm,
        };
      } catch (e) {
        return {
          pickupCutoffTime: new UntypedFormGroup(
            {
              hours: new UntypedFormControl(null, [Validators.min(1), Validators.max(12)]),
              minutes: new UntypedFormControl(null, [Validators.min(0), Validators.max(59)]),
              units: new UntypedFormControl('AM'),
            },
            hoursValidator(),
          ),
        };
      }
    },
    applySubForm: (generalForm: UntypedFormGroup) => {
      const values = generalForm?.value?.pickupCutoffTime;
      const shopTimeZoneOffset = getTimeZoneOffset(generalForm.value?.ianaTimeZone);

      if (isNumber(values?.hours) && isNumber(values.minutes) && values.units && isNumber(shopTimeZoneOffset)) {
        const cutoffTimeDate = new Date();
        cutoffTimeDate.setMinutes(+values.minutes);
        cutoffTimeDate.setSeconds(0);
        let convertedHours = +values.hours;
        if (values.units === 'PM' && values.hours < 12) {
          convertedHours = convertedHours + 12;
        } else if (values.units === 'AM' && +values.hours === 12) {
          convertedHours = convertedHours - 12;
        }
        const year = cutoffTimeDate.getFullYear();
        const month = cutoffTimeDate.getMonth();
        const day = cutoffTimeDate.getDate();
        const hour = convertedHours;
        const minutes = +values.minutes;

        cutoffTimeDate.setHours(convertedHours);
        const timeWithShopTimeZone = moment.tz(
          { year, month, day, hour, minutes },
          generalForm.value?.ianaTimeZone ?? 'UTC',
        );
        const cuttoffTimeInUTC = timeWithShopTimeZone.clone().utc();

        const editableValues = [{ path: 'metadata.pickupCutoffTime', value: cuttoffTimeInUTC }];
        return editableValues;
      }

      return [{ path: 'metadata.pickupCutoffTime', value: null }];
    },
  },
  {
    name: 'greetingCard',
    label: 'Add greeting card',
    description: 'Let customers include a greeting card with their order',
    dataName: 'metadata.greetingCard',
    type: FieldType.TOGGLE,
    editable: true,
    default: false,
    templateRef: 'additionalSubFormFields',
    nestedFormControl: 'active',
    subFormFields: [
      {
        dataName: 'from',
        type: FieldType.RADIO,
        editable: true,
        default: 'note',
        options: [
          {
            label: 'note',
            value: 'note',
          },
          {
            label: 'note2',
            value: 'note2',
          },
        ],
      },
      {
        label: 'Text source',
        dataName: 'source_key',
        type: FieldType.INPUT,
        placeholder: 'For example: greeting_card',
        editable: true,
        default: '',
      },
      {
        dataName: 'openInFulfillFlow',
        type: FieldType.CHECKBOX,
        label: 'Include printing greeting cards as part of PDQ fulfillment flow',
        editable: true,
        default: 'false',
      },
    ],
    generateSubForm: (value, shop) => {
      const { metadata } = shop;
      const fields: any = {
        active: new UntypedFormControl(!!(metadata?.greetingCard?.active ?? false)),
        from: new UntypedFormControl(metadata?.greetingCard?.from ?? 'note'),
        source_key: new UntypedFormControl(metadata?.greetingCard?.source_key ?? ''),
        openInFulfillFlow: new UntypedFormControl(metadata?.greetingCard?.openInFulfillFlow ?? false),
        type: new UntypedFormControl(metadata.greetingCard?.type ?? 'perOrder'),
      };

      const subForm = new UntypedFormGroup(fields);

      return {
        greetingCard: subForm,
      };
    },
    applySubForm: (formGroup) => {
      const values: any = {
        active: formGroup.value.greetingCard.active,
        type: formGroup.value.greetingCard.type,
        source_key: formGroup.value.greetingCard.source_key,
        openInFulfillFlow: formGroup.value.greetingCard.openInFulfillFlow,
        from: formGroup.value.greetingCard.from,
      };
      return [{ path: 'metadata.greetingCard', value: values }];
    },
  },
  {
    name: 'isSupportTeamNotesEcommerceUpdate',
    label: 'Team notes',
    description: 'Sync internal team notes with Shopify order notes',
    dataName: 'metadata.isSupportTeamNotesEcommerceUpdate',
    type: FieldType.TOGGLE,
    editable: true,
    displayCondition: (countryCode: CountryCode, platform: ECOMMERCES) => {
      return platform === ECOMMERCES.SHOPIFY;
    },
  },
  {
    name: 'isSupportMultipleReturnLabels',
    label: 'Multiple return labels',
    description: 'Allow more than one return label per item',
    dataName: 'metadata.isSupportMultipleReturnLabels',
    type: FieldType.TOGGLE,
    editable: true,
    displayCondition: (countryCode: CountryCode, platform: ECOMMERCES) => {
      return platform === ECOMMERCES.SHOPIFY;
    },
  },
  {
    name: 'disableManualFulfillment',
    label: 'Disable manual fulfillment',
    description: 'Prevent pickers from manually marking items as fulfilled without scanning',
    dataName: 'metadata.disableManualFulfillment',
    type: FieldType.TOGGLE,
    editable: true,
    default: false,
  },
];

function hoursValidator(): ValidatorFn {
  return (control: UntypedFormGroup): { [key: string]: any } | null => {
    const hoursControl = control.controls.hours;
    const minutesControl = control.controls.minutes;

    const hoursNotSpecified = hoursControl.value === null || hoursControl.value === '';
    const minutesNotSpecified = minutesControl.value === null || minutesControl.value === '';
    if (hoursNotSpecified && minutesNotSpecified) {
      control.setErrors(null);
      hoursControl.setErrors(null);
      minutesControl.setErrors(null);
      control.markAsPristine();
      hoursControl.markAsPristine();
      minutesControl.markAsPristine();
      return null;
    } else if (hoursNotSpecified || minutesNotSpecified) {
      const res: any = {};
      if (hoursNotSpecified) {
        res.hours = 'Hours value is invalid';
        hoursControl.setErrors({ hours: 'Hours value is invalid' });
      }
      if (minutesNotSpecified) {
        res.minutes = 'Minutes value is invalid';
        minutesControl.setErrors({ minutes: 'Minutes value is invalid' });
      }
      return res;
    }

    return null;
  };
}
